import { useMemo } from 'react';
import type { FrequencyEnum, ProxyTypeEnum } from 'venn-api';
import { TooltipPosition } from 'venn-ui-kit';
import { cn } from '@/lib/utils';
import {
  PROXY_TYPE_GROUPS,
  getExtrapolateFromValue,
  isSecondLayerProxyType,
  createSecondLayerProxyTypeValue,
  type FirstLayerProxyTypeValue,
  type SecondLayerProxyTypeValue,
  type ProxyTypeDetails,
  validateLayeredProxyTypeForInvestments,
  type LayeredProxyTypeValidation,
} from 'venn-utils';
import type { ProxyTypeDropdownItem } from '../components/ProxyTypeDropdown';
import type { ReturnsRangeInfo } from '../utils';
import { resolveDisabledProxyReasonMessage } from '../utils';
import { useProxyTypeValidation, type ProxyTypeValidation } from '../useProxyTypeValidation';
import type { FundToBulkProxy, FundToProxy } from '../../types';

interface UseProxyTypeDropdownItemsOptions {
  investments: (FundToProxy | FundToBulkProxy)[];
  rawInvestmentRanges?: (ReturnsRangeInfo | null)[];
  selectedProxyRange?: ReturnsRangeInfo | null;
  withInvalidTypes?: boolean;
  withExtrapolation?: boolean;
}

const getQaClassName = (proxyType: ProxyTypeEnum, extrapolate: boolean, prefix: string) => {
  const extrapolateSuffix = extrapolate ? '-extrapolate' : '';
  return `${prefix}-${proxyType.toLowerCase()}${extrapolateSuffix}`;
};

const getValidationInfo = (
  investments: { id: string }[],
  proxyTypeValidationResults: ProxyTypeValidation[ProxyTypeEnum] | undefined,
  layeredProxyTypeValidation: LayeredProxyTypeValidation,
) => {
  const investmentIdToProxyTypeValidation = proxyTypeValidationResults
    ? Object.fromEntries(proxyTypeValidationResults.investmentInfo.map((ii) => [ii.investmentId, ii]))
    : {};
  const investmentIdToLayeredProxyValidation = Object.fromEntries(
    layeredProxyTypeValidation.investmentInfo.map((ii) => [ii.investmentId, ii]),
  );

  const investmentInfo = investments.map((investment) => {
    const proxyTypeValidation = investmentIdToProxyTypeValidation[investment.id];
    const layeredProxyValidation = investmentIdToLayeredProxyValidation[investment.id];
    return {
      disabledMessage: resolveDisabledProxyReasonMessage(
        layeredProxyValidation?.disabledMessage || proxyTypeValidation?.disabledMessage,
      ),
      hasProxyTypeError: !layeredProxyValidation?.isValid || proxyTypeValidation?.hasProxyTypeError,
      investmentId: investment.id,
    };
  });
  const investmentInfoWithErrors = investmentInfo.filter((ii) => ii.hasProxyTypeError);
  return {
    isValid: investmentInfoWithErrors.length < investmentInfo.length,
    firstMessage: investmentInfoWithErrors.find((ii) => ii.disabledMessage)?.disabledMessage,
    investmentInfo,
  };
};

interface BuildItemsParams<T extends FirstLayerProxyTypeValue> {
  proxyTypeValidationResults: ProxyTypeValidation;
  withInvalidTypes: boolean | undefined;
  withExtrapolation: boolean | undefined;
  isAllowedType: (proxyType: ProxyTypeEnum) => boolean;
  createValue: (detail: ProxyTypeDetails) => T;
  qaPrefix: string;
  investments: { frequency: FrequencyEnum | undefined; investmentSource: string; id: string }[];
  proxyLayer: 'first' | 'second';
}

function buildDropdownItems<T extends FirstLayerProxyTypeValue>({
  proxyTypeValidationResults,
  withInvalidTypes,
  withExtrapolation,
  isAllowedType,
  createValue,
  qaPrefix,
  investments,
  proxyLayer,
}: BuildItemsParams<T>): ProxyTypeDropdownItem<T>[] {
  const investmentsWithFrequency = investments.filter(
    (investment): investment is { frequency: FrequencyEnum; investmentSource: string; id: string } =>
      investment.frequency !== undefined,
  );
  return PROXY_TYPE_GROUPS.flatMap((proxyTypeGroup) => {
    const filteredProxyTypeGroup = proxyTypeGroup.filter((detail) => {
      const { proxyType } = detail.value;
      const extrapolate = getExtrapolateFromValue(detail.value);

      if (!isAllowedType(proxyType)) {
        return false;
      }

      if (!withExtrapolation && extrapolate) {
        return false;
      }

      if (!withInvalidTypes) {
        const layeredProxyTypeValidation = validateLayeredProxyTypeForInvestments({
          proxy: { proxyType, extrapolate, proxyLayer },
          investments: investmentsWithFrequency,
        });
        const { isValid } = getValidationInfo(
          investmentsWithFrequency,
          proxyTypeValidationResults[proxyType],
          layeredProxyTypeValidation,
        );

        if (!isValid) {
          return false;
        }
      }

      return true;
    });
    return filteredProxyTypeGroup.map((detail, index) => {
      const { proxyType } = detail.value;
      const extrapolate = getExtrapolateFromValue(detail.value);

      const layeredProxyTypeValidation = validateLayeredProxyTypeForInvestments({
        proxy: { proxyType, extrapolate, proxyLayer },
        investments: investmentsWithFrequency,
      });

      const { isValid, firstMessage: validationMessage } = getValidationInfo(
        investmentsWithFrequency,
        proxyTypeValidationResults[proxyType],
        layeredProxyTypeValidation,
      );

      return {
        ...detail,
        value: createValue(detail),
        description: isValid ? detail.description : validationMessage,
        disabled: detail.disabled || !isValid,
        tooltipContent: isValid ? validationMessage : undefined,
        tooltipPosition: TooltipPosition.Right,
        style: index < filteredProxyTypeGroup.length - 1 ? { borderBottom: 'none' } : undefined,
        className: cn(
          getQaClassName(proxyType, extrapolate, qaPrefix),
          !isValid && '[&>*:first-child]:pointer-events-none',
        ),
      };
    });
  });
}

export function useFirstLayerProxyTypeDropdownItems(
  options: UseProxyTypeDropdownItemsOptions,
): ProxyTypeDropdownItem<FirstLayerProxyTypeValue>[] {
  const { investments, rawInvestmentRanges, selectedProxyRange, withInvalidTypes, withExtrapolation } = options;

  const proxyTypeValidationResults = useProxyTypeValidation(
    investments,
    rawInvestmentRanges ?? [],
    selectedProxyRange,
    undefined,
  );

  return useMemo(
    () =>
      buildDropdownItems<FirstLayerProxyTypeValue>({
        proxyTypeValidationResults,
        withInvalidTypes,
        withExtrapolation,
        isAllowedType: () => true,
        createValue: (detail) => detail.value,
        qaPrefix: 'qa-proxy-option',
        investments: investments.map((investment, i) => ({
          frequency: rawInvestmentRanges?.[i]?.frequency ?? investment.returnFrequency,
          investmentSource: investment.investmentSource,
          id: investment.id,
        })),
        proxyLayer: 'first',
      }),
    [investments, proxyTypeValidationResults, rawInvestmentRanges, withExtrapolation, withInvalidTypes],
  );
}

export function useSecondLayerProxyTypeDropdownItems(
  options: UseProxyTypeDropdownItemsOptions,
): ProxyTypeDropdownItem<SecondLayerProxyTypeValue>[] {
  const { investments, rawInvestmentRanges, selectedProxyRange, withInvalidTypes, withExtrapolation } = options;

  const proxyTypeValidationResults = useProxyTypeValidation(
    investments,
    rawInvestmentRanges ?? [],
    selectedProxyRange,
    undefined,
  );

  return useMemo(
    () =>
      buildDropdownItems<SecondLayerProxyTypeValue>({
        proxyTypeValidationResults,
        withInvalidTypes,
        withExtrapolation,
        isAllowedType: isSecondLayerProxyType,
        createValue: (detail) => {
          const { proxyType } = detail.value;
          if (!isSecondLayerProxyType(proxyType)) {
            throw new Error(`Invalid second layer proxy type: ${proxyType}`);
          }
          return createSecondLayerProxyTypeValue(proxyType, getExtrapolateFromValue(detail.value));
        },
        qaPrefix: 'qa-second-layer-proxy-option',
        investments: investments.map((investment, i) => ({
          frequency: rawInvestmentRanges?.[i]?.frequency ?? investment.returnFrequency,
          investmentSource: investment.investmentSource,
          id: investment.id,
        })),
        proxyLayer: 'second',
      }),
    [investments, proxyTypeValidationResults, rawInvestmentRanges, withExtrapolation, withInvalidTypes],
  );
}
